import { useMutationObserver } from "@mantine/hooks";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Check if the input element is overflowed.
 * @param inputElement  Input element.
 * @returns Whether a boolean indicating if the input element is overflowed.
 */
export const useIsInputOverflowed = (inputElement: HTMLInputElement | null) => {
    const [isOverflowed, setIsOverflowed] = useState(false);
    const valueRef = useRef<string>("");
    const rafIdRef = useRef<number | null>(null);

    const placeholder = inputElement?.placeholder;

    const updateIsOverflowed = useCallback(() => {
        if (inputElement === null) {
            setIsOverflowed(false);
            return;
        }

        // Cancel any pending animation frame
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
        }

        // Use requestAnimationFrame to ensure DOM has updated
        rafIdRef.current = requestAnimationFrame(() => {
            if (inputElement === null) {
                return;
            }

            const initialValue = inputElement.value;
            inputElement.value = inputElement.value || inputElement.placeholder;

            const overflowed = inputElement.scrollWidth - inputElement.clientWidth > 2;
            inputElement.value = initialValue;

            setIsOverflowed(overflowed);
            rafIdRef.current = null;
        });
    }, [inputElement]);

    // Update when input element, or placeholder changes
    useLayoutEffect(() => {
        updateIsOverflowed();
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, [inputElement, placeholder, updateIsOverflowed]);

    // Handle value changes (from either user input or programmatic changes)
    const handleValueChange = useCallback(() => {
        if (inputElement === null) {
            return;
        }

        const currentValue = inputElement.value;
        // Only update if the value actually changed
        if (currentValue !== valueRef.current) {
            valueRef.current = currentValue;
            updateIsOverflowed();
        }
    }, [inputElement, updateIsOverflowed]);

    // Use MutationObserver to detect programmatic value attribute changes
    useMutationObserver(
        (mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === "attributes" && mutation.attributeName === "value") {
                    handleValueChange();
                    break;
                }
            }
        },
        { attributes: true, attributeFilter: ["value"] },
        inputElement
    );

    // Listen for user input events
    useEffect(() => {
        if (inputElement === null) {
            return;
        }

        // Initialize the value ref
        valueRef.current = inputElement.value;

        inputElement.addEventListener("input", handleValueChange);
        inputElement.addEventListener("change", handleValueChange);

        return () => {
            inputElement.removeEventListener("input", handleValueChange);
            inputElement.removeEventListener("change", handleValueChange);

            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, [inputElement, handleValueChange]);

    return isOverflowed;
};
